/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/

import Vue from "vue";
import Router from "vue-router";
import store from "./store/store";

Vue.use(Router);

const helpCenter = import("@/views/helpcenter/HelpCenter.vue");
const helpCenterGroup = import("@/views/helpcenter/HelpCenterGroup.vue");
const helpCenterArticle = import("@/views/helpcenter/HelpcenterArticle.vue");

const router = new Router({
  mode: "history",
  base: "/app/",
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
  routes: [
    {
      // =============================================================================
      // MAIN LAYOUT ROUTES
      // =============================================================================
      path: "/customer",
      component: () => import("./layouts/main/Main.vue"),
      children: [
        // =============================================================================
        // Theme Routes
        // =============================================================================
        {
          path: "dashboard",
          name: "customer-dashboard",
          component: () => import("./views/customer/dashboard/Dashboard.vue"),
          meta: {
            rule: "isCustomer",
            title: "Customer Dashboard",
          },
        },
        {
          path: "order",
          name: "create-order",
          component: () => import("./views/customer/order/Create.vue"),
          meta: {
            rule: "isCustomer",
            title: "Create Order",
          },
        },
        {
          path: "bulk-order",
          name: "bulk-order",
          component: () => import("./views/customer/order/CreateBulk.vue"),
          meta: {
            rule: "isCustomer",
            title: "Create Bulk Order",
          },
        },
        {
          path: "order/details",
          name: "order-details",
          component: () => import("./views/customer/order/Details.vue"),
          meta: {
            rule: "isCustomer",
            title: "Order Detail",
          },
        },
        {
          path: "order/details/:orderID",
          name: "order-detail",
          component: () => import("./views/customer/order/Details.vue"),
          meta: {
            rule: "isCustomer",
            title: "Order Detail",
          },
        },
        {
          path: "order/:orderID/reschedule",
          name: "order-reschedule",
          component: () => import("./views/customer/order/Reschedule.vue"),
          meta: {
            rule: "isCustomer",
            title: "Reschedule Order",
          },
        },
        {
          path: "order/same-day",
          name: "create-order-same-day",
          component: () => import("./views/customer/order/SameDay.vue"),
          meta: {
            rule: "isCustomer",
            title: "Create Same Day Order",
          },
        },
        {
          path: "order/one-to-three-days",
          name: "create-order-one-to-three-days",
          component: () => import("./views/customer/order/OneToThreeDays.vue"),
          meta: {
            rule: "isCustomer",
            title: "Create One to Three Days Order",
          },
        },
        {
          path: "order/next-day-premium",
          name: "create-order-next-day-premium",
          component: () => import("./views/customer/order/NextDayPremium.vue"),
          meta: {
            rule: "isCustomer",
            title: "Create Next Day Premium Order",
          },
        },
        {
          path: "order/international",
          name: "create-order-international",
          component: () => import("./views/customer/order/International.vue"),
          meta: {
            rule: "isCustomer",
            title: "Create International Order",
          },
        },
        {
          path: "order/on-demand",
          name: "create-order-on-demand",
          component: () => import("./views/customer/order/OnDemand.vue"),
          meta: {
            rule: "isCustomer",
            title: "Create On Demand Order",
          },
        },
        {
          path: "order/sicepat-siuntung",
          name: "create-order-sicepat-siuntung",
          component: () => import("./views/customer/order/SiCepatSiUntung.vue"),
          meta: {
            rule: "isCustomer",
            title: "Create SiCepat SiUntung Order",
          },
        },
        {
          path: "order/adhoc",
          name: "create-order-adhoc",
          component: () => import("./views/customer/order/Adhoc.vue"),
          meta: {
            rule: "isCustomer",
            title: "Create Adhoc Order",
          },
        },
        {
          path: "wallet",
          name: "wallet",
          component: () => import("./views/customer/payment/Wallet.vue"),
          meta: {
            rule: "isCustomer",
            title: "Customer Payment Option",
          },
        },
        {
          path: "setting-resource",
          name: "setting-resource",
          component: () => import("./views/customer/tool/SettingResource.vue"),
          meta: {
            rule: "isCustomer",
            title: "Customer Setting & Resource",
          },
        },
      ],
    },
    {
      path: "/driver",
      component: () => import("./layouts/main/Main.vue"),
      children: [
        {
          path: "",
          name: "driver-dashboard",
          component: () => import("@/views/driver/Download.vue"),
          meta: {
            rule: "isDriver",
            title: "Agent Dashboard",
          },
        },
        {
          path: "download",
          name: "driver-download",
          component: () => import("@/views/driver/Download.vue"),
          meta: {
            rule: "isUser",
            title: "Agent App Download",
          },
        },
      ],
    },
    {
      path: "/help-center",
      component: () => import("./layouts/main/Main.vue"),
      children: [
        {
          path: "",
          name: "customer-help-center",
          component: () => helpCenter,
          meta: {
            rule: "isCustomer",
            title: "Help Center",
          },
        },
        {
          path: "group/:group_id",
          name: "customer-help-center-group",
          component: () => helpCenterGroup,
          meta: {
            rule: "isCustomer",
            title: "Help Center Group",
          },
        },
        {
          path: "article/:slug",
          name: "customer-help-center-article",
          component: () => helpCenterArticle,
          meta: (route) => ({
            rule: "isCustomer",
            title: "Help Center Article",
          }),
        },
      ],
    },
    {
      path: "/contact-us",
      component: () => import("./layouts/main/Main.vue"),
      children: [
        {
          path: "",
          name: "customer-contact-us",
          component: () => import("./views/helpcenter/HelpCenterContactUs.vue"),
          meta: {
            rule: "isCustomer",
            title: "Contact Us",
          },
        },
      ],
    },
    {
      path: "/user-setting",
      component: () => import("./layouts/main/Main.vue"),
      children: [
        {
          path: "",
          name: "user-setting",
          component: () => import("./views/userSetting/UserSetting.vue"),
          meta: {
            rule: "isCustomer",
            title: "User Setting",
          },
        },
      ],
    },
    {
      path: "/notifications",
      component: () => import("./layouts/main/Main.vue"),
      children: [
        {
          path: "",
          name: "notification",
          component: () => import("@/views/customer/dashboard/Notification.vue"),
          meta: {
            rule: "isCustomer",
            title: "Notifications",
          },
        },
      ],
    },
    {
      path: "/chats",
      component: () => import("./layouts/main/Main.vue"),
      children: [
        {
          path: "",
          name: "chat",
          component: () => import("@/views/customer/chat/Chat.vue"),
          meta: {
            rule: "isCustomer",
            title: "Chat",
          },
        },
      ],
    },
    {
      path: "",
      component: () => import("@/layouts/full-page/AuthLayout.vue"),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: "/login",
          name: "login",
          component: () => import("@/views/auth/Login.vue"),
          meta: {
            rule: "isPublic",
            title: "Login",
          },
          beforeEnter: (to, from, next) => {
            redirectDashboard(to, from, next);
          },
        },
        {
          path: "/register",
          name: "register",
          component: () => import("@/views/auth/Register.vue"),
          meta: {
            rule: "isPublic",
            title: "Account Registration",
          },
          beforeEnter: (to, from, next) => {
            redirectDashboard(to, from, next);
          },
        },
        {
          path: "/account/forget-password",
          name: "forget_password",
          component: () => import("@/views/auth/ForgetPassword.vue"),
          meta: {
            rule: "isPublic",
            title: "Forget Password",
          },
          beforeEnter: (to, from, next) => {
            redirectDashboard(to, from, next);
          },
        },
        {
          path: "/setup-account",
          name: "setup-account",
          component: () => import("@/views/auth/SetupAccount.vue"),
          meta: {
            rule: "isUser",
            title: "Setup Initial Account",
          },
        },
      ],
    },

    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    {
      path: "",
      component: () => import("@/layouts/full-page/FullPage.vue"),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: "/account/change-password/:verification_token",
          name: "change_password",
          component: () => import("@/views/auth/ChangePassword.vue"),
          meta: {
            rule: "isPublic",
            title: "Change Password",
          },
          beforeEnter: (to, from, next) => {
            redirectDashboard(to, from, next);
          },
        },
        {
          path: "/account/verified/:verification_token",
          name: "verified_account",
          component: () => import("@/views/auth/VerifiedAccount.vue"),
          meta: {
            rule: "isPublic",
            title: "Verified Account Success",
          },
          beforeEnter: (to, from, next) => {
            redirectDashboard(to, from, next);
          },
        },
        {
          path: "/error/404",
          name: "page-error-404",
          component: () => import("@/views/errors/Error404.vue"),
          meta: {
            rule: "isPublic",
            title: "Page Not Found",
          },
        },
        {
          path: "/error/500",
          name: "page-error-500",
          component: () => import("@/views/errors/Error500.vue"),
          meta: {
            rule: "isPublic",
            title: "Internal Server Error",
          },
        },
        {
          path: "/error/503",
          name: "page-error-503",
          component: () => import("@/views/errors/Error503.vue"),
          meta: {
            rule: "isPublic",
            title: "Service Not Available",
          },
        },
        {
          path: "/rates",
          name: "customer-public-rates",
          component: () => import("@/views/customer/tool/calculator/PublicRates.vue"),
          meta: {
            rule: "isPublic",
            title: "Pricing Rate",
          },
        },
      ],
    },
    {
      path: "",
      component: () => import("@/layouts/container/Container.vue"),
      children: [
        {
          path: "/calculator",
          name: "customer-public-calculator",
          component: () => import("@/views/customer/tool/calculator/PublicRates.vue"),
          meta: {
            rule: "isPublic",
            title: "Rate Calculator",
          },
        },
        {
          path: "/rates-calculator",
          name: "customer-public-rates-calculator",
          component: () => import("@/views/customer/tool/calculator/PublicRates.vue"),
          meta: {
            rule: "isPublic",
            title: "Rate Calculator",
          },
        },
        {
          path: "/helpcenter",
          name: "public-help-center",
          component: () => helpCenter,
          meta: {
            rule: "isPublic",
            title: "Help Center",
          },
        },
        {
          path: "/helpcenter/:slug",
          name: "public-help-center-article",
          component: () => helpCenterArticle,
          meta: {
            rule: "isPublic",
            title: "Help Center Article",
          },
        },
        {
          path: "/helpcenter/group/details",
          name: "public-help-center-group",
          component: () => helpCenterGroup,
          meta: {
            rule: "isPublic",
            title: "Help Center Group",
          },
        },
      ],
    },
    // Redirect to 404 page, if no match found
    {
      path: "*",
      redirect: "/error/404",
      meta: {
        rule: "isPublic",
        title: "Page Not Found",
      },
    },
  ],
});

function redirectDashboard(to, from, next) {
  if (store.getters.isLoggedIn) next({ name: "customer-dashboard" });
  next();
}

const DEFAULT_TITLE = "Web Application Portal";
router.afterEach((to, from) => {
  // Remove initial loading
  const appLoading = document.getElementById("loading-bg");
  if (appLoading) {
    appLoading.style.display = "none";
  }
  Vue.nextTick(() => {
    document.title = process.env.MIX_APP_NAME + " - " + (to.meta.title || DEFAULT_TITLE);
  });
});

router.beforeEach(async (to, from, next) => {
  if (store.state.appModule.maintenanceMode == null) {
    await store.dispatch("checkMaintenanceMode").catch((err) => {
      if (store.getters.isUnderMaintenance) {
        return router.push({ name: "page-error-503" });
      }
    });
  }

  if (
    to.name == "customer-help-center" ||
    to.name == "customer-help-center-group" ||
    to.name == "customer-help-center-article"
  ) {
    store.dispatch("updateHelpCenterCustomizer", false);
  } else {
    store.dispatch("updateHelpCenterCustomizer", true);
  }

  if (!store.getters.getUserCountry) {
    store.dispatch("initializeCountryLocation");
  }

  //  If auth required, check login. If login fails redirect to login page
  if (to.meta.rule && to.meta.rule != "isPublic") {
    if (!store.getters.isLoggedIn) {
      router.push({ name: "login" }).catch((err) => {});
    } else {
      if (!store.getters.getUser) {
        await store.dispatch("retrieveUserInfo").catch((err) => {
          const status_code = err.response ? err.response.data.status_code : null;
          if (status_code == 403) {
            store.dispatch("removeUserInfo");
            return router.push({ name: "login" }).catch((err) => {});
          } else {
            return router.push({ name: "page-error-500" }).catch((err) => {});
          }
        });
      }

      if (to.meta.rule == "isCustomer") {
        if (!store.getters.getCustomer) {
          await store
            .dispatch("retrieveCustomerInfo")
            .then(() => {
              if (!store.getters.getCustomer) {
                return router.push({ name: "setup-account" }).catch((err) => {});
              }
              if (store.getters.getCustomer.status == "BLOCKED") {
                return router.push({ name: "login" }).catch((err) => {});
              }
            })
            .catch(() => {
              store.dispatch("removeUserInfo");
              return router.push({ name: "login" }).catch((err) => {});
            });
        }
      }

      await store.dispatch("initializeAppModule");
    }
  }

  return next();
});

export default router;
